import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import {
  IconExternalLink,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUser,
  IconUsers,
} from '@/components/icons'
import {
  ActionButton,
  AppModal,
  DataTable,
  DataTableScroll,
  DataTbody,
  DataTd,
  DataTh,
  DataThead,
  DataTr,
  EmptyState,
  PageCenterLoader,
  PageShell,
  PageToolbar,
  PaginationBar,
} from '@/components/ui'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'
import { identifierTypeLabel, visibleCustomerIdentifiers } from '@/lib/identifier-labels'
import {
  mergeWithDefaultPermissions,
  hasPermission,
  loadMyPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import { parseApiDateUtc } from '@/lib/api-date'
import { formatDateTimeDot, statusLabel, ticketPathSegmentFromListItem } from '@/lib/ticket-display'
import { useWorkspaceTabs } from '@/lib/workspace-tabs-context'
import type {
  MyPermissionsResponse,
  ProjectRow,
  ProjectUserIdentifierRow,
  ProjectUserIdentifiersListUser,
  ProjectUserIdentifiersListResponse,
  TicketListItem,
} from '@/types/api'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const
const USERS_PAGE_SIZE_LS = 'sd2_users_page_size'

function readSavedPageSize(): number {
  try {
    const n = parseInt(localStorage.getItem(USERS_PAGE_SIZE_LS) || '', 10)
    if (PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])) return n
  } catch {
    /* ignore */
  }
  return 50
}

const IDENTIFIER_TYPES: { value: string; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'telegram_username', label: 'Telegram @username' },
  { value: 'telegram_id', label: 'Telegram ID' },
  { value: 'whatsapp_number', label: 'WhatsApp' },
  { value: 'phone', label: 'Телефон' },
  { value: 'user_id', label: 'ID пользователя' },
  { value: 'widget_user_id', label: 'ID пользователя (виджет)' },
  { value: 'device_id', label: 'Device ID' },
  { value: 'custom', label: 'Пользовательский' },
]

export function UsersPage() {
  const { user } = useAuth()
  const { openOrSelectTab } = useWorkspaceTabs()

  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm)
  }, [user?.role])
  const effectivePerm = mergeWithDefaultPermissions(perm, user?.role)
  const canUsers = hasPermission(effectivePerm, 'access_users')

  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [projectId, setProjectId] = useState('')

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(readSavedPageSize)
  const [search, setSearch] = useState('')
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [searchApplied, setSearchApplied] = useState('')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [list, setList] = useState<ProjectUserIdentifiersListResponse | null>(null)

  const [manageOpen, setManageOpen] = useState(false)
  const [manageProjectId, setManageProjectId] = useState('')
  const [manageUserId, setManageUserId] = useState('')
  const [manageTitle, setManageTitle] = useState('')
  const [customerForm, setCustomerForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    note: '',
  })
  const [identifiers, setIdentifiers] = useState<ProjectUserIdentifierRow[]>([])
  const [selectedIdentifierIds, setSelectedIdentifierIds] = useState<string[]>([])
  const [idLoading, setIdLoading] = useState(false)
  const [newIdType, setNewIdType] = useState('email')
  const [newIdValue, setNewIdValue] = useState('')
  const [newIdDisplayName, setNewIdDisplayName] = useState('')

  const [ticketsOpen, setTicketsOpen] = useState(false)
  const [ticketsTitle, setTicketsTitle] = useState('')
  const [ticketsRows, setTicketsRows] = useState<TicketListItem[]>([])
  const [ticketsLoading, setTicketsLoading] = useState(false)

  const [addOpen, setAddOpen] = useState(false)
  const [addCustomerId, setAddCustomerId] = useState('')
  const [addCustomerManual, setAddCustomerManual] = useState('')
  const [addIdType, setAddIdType] = useState('email')
  const [addIdValue, setAddIdValue] = useState('')
  const [addDisplayName, setAddDisplayName] = useState('')
  const [addActive, setAddActive] = useState(true)
  const [customerOptions, setCustomerOptions] = useState<ProjectUserIdentifiersListUser[]>([])
  const [addSaving, setAddSaving] = useState(false)

  useEffect(() => {
    let alive = true
    void api.projects().then((p) => {
      if (alive) setProjects(Array.isArray(p) ? p : [])
    })
    return () => {
      alive = false
    }
  }, [])

  const projectName = useMemo(() => {
    const p = projects.find((x) => x.id === projectId)
    return p?.name || ''
  }, [projects, projectId])

  const loadList = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    setError(null)
    try {
      const res = await api.projectUserIdentifiersList(projectId, {
        page,
        page_size: pageSize,
        search: searchApplied || undefined,
      })
      setList(res)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Ошибка загрузки')
      setList(null)
    } finally {
      setLoading(false)
    }
  }, [projectId, page, pageSize, searchApplied])

  useEffect(() => {
    if (!projectId) {
      setList(null)
      return
    }
    void loadList()
  }, [projectId, loadList])

  const onSearchChange = (v: string) => {
    setSearch(v)
    if (searchDebounce.current) clearTimeout(searchDebounce.current)
    searchDebounce.current = setTimeout(() => {
      setSearchApplied(v.trim())
      setPage(1)
    }, 400)
  }

  const totalPages = Math.max(1, Math.ceil((list?.total ?? 0) / pageSize))

  const openManage = async (row: ProjectUserIdentifiersListUser, pid: string) => {
    setManageProjectId(pid)
    setManageUserId(row.user_id)
    setManageTitle(row.user_name || row.user_email || row.user_id)
    setSelectedIdentifierIds([])
    setManageOpen(true)
    setIdLoading(true)
    try {
      const c = await api.customer(row.user_id).catch(() => null)
      if (c) {
        setCustomerForm({
          first_name: c.first_name || '',
          last_name: c.last_name || '',
          email: c.email || '',
          phone: c.phone || '',
          note: c.note || '',
        })
      } else {
        setCustomerForm({
          first_name: '',
          last_name: '',
          email: row.user_email || '',
          phone: '',
          note: '',
        })
      }
      const ids = await api.projectUserIdentifiers(pid, row.user_id)
      setIdentifiers(visibleCustomerIdentifiers(Array.isArray(ids) ? ids : []))
    } catch {
      showNotification({ title: 'Ошибка', message: 'Не удалось загрузить данные клиента', color: 'red' })
      setIdentifiers([])
    } finally {
      setIdLoading(false)
    }
  }

  const reloadIdentifiers = async () => {
    if (!manageProjectId || !manageUserId) return
    const ids = await api.projectUserIdentifiers(manageProjectId, manageUserId)
    setIdentifiers(visibleCustomerIdentifiers(Array.isArray(ids) ? ids : []))
  }

  const saveCustomer = async () => {
    try {
      await api.customerUpdate(manageUserId, {
        first_name: customerForm.first_name.trim() || null,
        last_name: customerForm.last_name.trim() || null,
        email: customerForm.email.trim() || null,
        phone: customerForm.phone.trim() || null,
        note: customerForm.note.trim() || null,
      })
      showNotification({ title: 'Сохранено', message: 'Данные клиента обновлены.', color: 'green' })
      void loadList()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
        color: 'red',
      })
    }
  }

  const addIdentifier = async () => {
    const v = newIdValue.trim()
    if (!v) return
    try {
      await api.projectUserIdentifierCreate(manageProjectId, {
        user_id: manageUserId,
        identifier_type: newIdType,
        identifier_value: v,
        display_name: newIdType === 'custom' ? newIdDisplayName.trim() || null : null,
      })
      setNewIdValue('')
      setNewIdDisplayName('')
      await reloadIdentifiers()
      void loadList()
      showNotification({ title: 'Готово', message: 'Идентификатор добавлен.', color: 'green' })
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось добавить',
        color: 'red',
      })
    }
  }

  const removeIdentifier = (identifierId: string) => {
    openConfirmModal({
      title: 'Удалить идентификатор?',
      children: <Text size="sm">Действие можно повторить только добавлением заново.</Text>,
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.projectUserIdentifierDelete(manageProjectId, identifierId)
          setSelectedIdentifierIds((prev) => prev.filter((x) => x !== identifierId))
          await reloadIdentifiers()
          void loadList()
          showNotification({ title: 'Удалено', message: '', color: 'green' })
        } catch (e) {
          showNotification({
            title: 'Ошибка',
            message: e instanceof ApiError ? e.message : 'Не удалось удалить',
            color: 'red',
          })
        }
      },
    })
  }

  const toggleIdentifierSelected = (identifierId: string) => {
    setSelectedIdentifierIds((prev) =>
      prev.includes(identifierId) ? prev.filter((x) => x !== identifierId) : [...prev, identifierId],
    )
  }

  const allIdentifiersSelected =
    identifiers.length > 0 && identifiers.every((x) => selectedIdentifierIds.includes(x.id))
  const someIdentifiersSelected =
    identifiers.some((x) => selectedIdentifierIds.includes(x.id)) && !allIdentifiersSelected

  const toggleSelectAllIdentifiers = () => {
    if (allIdentifiersSelected) {
      setSelectedIdentifierIds([])
    } else {
      setSelectedIdentifierIds(identifiers.map((x) => x.id))
    }
  }

  const removeSelectedIdentifiers = () => {
    const ids = selectedIdentifierIds.filter((id) => identifiers.some((x) => x.id === id))
    if (ids.length === 0) return
    openConfirmModal({
      title: ids.length === 1 ? 'Удалить идентификатор?' : `Удалить идентификаторы (${ids.length})?`,
      children: (
        <Text size="sm">
          Будет удалено: {ids.length}. Действие можно повторить только добавлением заново.
        </Text>
      ),
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        let ok = 0
        let lastErr: string | null = null
        for (const identifierId of ids) {
          try {
            await api.projectUserIdentifierDelete(manageProjectId, identifierId)
            ok += 1
          } catch (e) {
            lastErr = e instanceof ApiError ? e.message : 'Ошибка удаления'
          }
        }
        setSelectedIdentifierIds([])
        await reloadIdentifiers()
        void loadList()
        if (ok === ids.length) {
          showNotification({ title: 'Удалено', message: `Удалено идентификаторов: ${ok}.`, color: 'green' })
        } else if (ok > 0) {
          showNotification({
            title: 'Частично удалено',
            message: `Удалено: ${ok} из ${ids.length}. ${lastErr || ''}`,
            color: 'yellow',
          })
        } else {
          showNotification({
            title: 'Ошибка',
            message: lastErr || 'Не удалось удалить',
            color: 'red',
          })
        }
      },
    })
  }

  const openTickets = async (row: ProjectUserIdentifiersListUser) => {
    setTicketsTitle(row.user_name || row.user_email || 'Клиент')
    setTicketsOpen(true)
    setTicketsLoading(true)
    setTicketsRows([])
    try {
      const raw = await api.customerPreviousTickets(row.user_id)
      const arr = Array.isArray(raw) ? raw : []
      arr.sort(
        (a, b) =>
          (parseApiDateUtc(b.created_at)?.getTime() ?? 0) -
          (parseApiDateUtc(a.created_at)?.getTime() ?? 0),
      )
      setTicketsRows(arr)
    } catch {
      showNotification({ title: 'Ошибка', message: 'Не удалось загрузить обращения', color: 'red' })
    } finally {
      setTicketsLoading(false)
    }
  }

  const openTicket = (t: TicketListItem) => {
    const ref = ticketPathSegmentFromListItem({
      id: t.id,
      ticket_number: t.ticket_number,
      project_slug: t.project_slug,
    })
    openOrSelectTab(`/tickets/${encodeURIComponent(ref)}`)
  }

  const openAdd = async () => {
    if (!projectId) return
    setAddCustomerId('')
    setAddCustomerManual('')
    setAddIdType('email')
    setAddIdValue('')
    setAddDisplayName('')
    setAddActive(true)
    setAddOpen(true)
    try {
      const res = await api.projectUserIdentifiersList(projectId, {
        page: 1,
        page_size: 1000,
        search: '',
      })
      setCustomerOptions(res.users ?? [])
    } catch {
      setCustomerOptions([])
    }
  }

  const submitAdd = async () => {
    const uid = addCustomerManual.trim() || addCustomerId
    if (!uid) {
      showNotification({ title: 'Клиент', message: 'Выберите клиента или введите UUID.', color: 'yellow' })
      return
    }
    const val = addIdValue.trim()
    if (!val) {
      showNotification({ title: 'Значение', message: 'Укажите значение идентификатора.', color: 'yellow' })
      return
    }
    setAddSaving(true)
    try {
      await api.projectUserIdentifierCreate(projectId, {
        user_id: uid,
        identifier_type: addIdType,
        identifier_value: val,
        display_name: addIdType === 'custom' ? addDisplayName.trim() || null : null,
        is_active: addActive,
      })
      setAddOpen(false)
      void loadList()
      showNotification({ title: 'Готово', message: 'Пользователь добавлен в проект.', color: 'green' })
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось создать',
        color: 'red',
      })
    } finally {
      setAddSaving(false)
    }
  }

  const removeRow = (row: ProjectUserIdentifiersListUser) => {
    const firstId = row.identifiers[0]?.id
    if (!firstId) return
    openConfirmModal({
      title: 'Удалить из проекта?',
      children: (
        <Text size="sm">
          Будет отвязан первый идентификатор у этого клиента в проекте. Продолжить?
        </Text>
      ),
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.projectUserIdentifierDelete(projectId, firstId)
          void loadList()
          showNotification({ title: 'Готово', message: '', color: 'green' })
        } catch (e) {
          showNotification({
            title: 'Ошибка',
            message: e instanceof ApiError ? e.message : 'Не удалось удалить',
            color: 'red',
          })
        }
      },
    })
  }

  if (!canUsers) {
    return (
      <PageShell>
        <Alert color="gray" variant="light" title="Нет доступа">
          Недостаточно прав для раздела «Пользователи».
        </Alert>
      </PageShell>
    )
  }

  const rows = list?.users ?? []

  return (
    <PageShell>
      <Stack gap="md">
        <PageToolbar
          leading={
            <Box>
              <Group gap="xs" wrap="nowrap">
                <IconUsers size={20} stroke={1.6} />
                <Text fw={700} size="lg">
                  Пользователи
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                Клиенты проекта и их email, Telegram, телефонные и пользовательские идентификаторы.
              </Text>
            </Box>
          }
        />

        <Card withBorder padding="md" radius="md">
          <PageToolbar
            leading={
            <Select
              label="Проект"
              data={[
                { value: '', label: 'Выберите проект' },
                ...projects.map((p) => ({ value: p.id, label: p.name || p.slug || p.id })),
              ]}
              value={projectId}
              onChange={(value) => {
                setProjectId(value || '')
                setPage(1)
              }}
              searchable
              allowDeselect={false}
              checkIconPosition="right"
              styles={{ root: { minWidth: 280 } }}
            />
            }
            trailing={
              projectId ? (
              <Button leftSection={<IconPlus size={16} stroke={1.5} />} onClick={() => void openAdd()}>
                Добавить пользователя
              </Button>
              ) : null
            }
          />
        </Card>

        {!projectId ? (
          <EmptyState
            icon={<IconUsers size={40} stroke={1.2} />}
            title="Выберите проект"
            description="Чтобы увидеть список пользователей с идентификаторами."
          />
        ) : loading && !list ? (
          <PageCenterLoader label="Загрузка" minHeight={200} />
        ) : (
          <Stack gap="sm">
            {error ? (
              <Alert color="red" variant="light" title="Ошибка">
                {error}
              </Alert>
            ) : null}

            <Card withBorder padding="sm" radius="md">
              <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
                <TextInput
                  label="Поиск"
                  placeholder="Имя, email, телефон, значение идентификатора…"
                  value={search}
                  onChange={(e) => onSearchChange(e.currentTarget.value)}
                  leftSection={<IconSearch size={14} stroke={1.5} />}
                  style={{ flex: '1 1 280px', minWidth: 200 }}
                />
                <Group gap="xs" wrap="wrap">
                  <Select
                    label="На странице"
                    data={PAGE_SIZE_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
                    value={String(pageSize)}
                    onChange={(value) => {
                      const n = parseInt(value || String(PAGE_SIZE_OPTIONS[0]), 10)
                      if (!PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])) return
                      try {
                        localStorage.setItem(USERS_PAGE_SIZE_LS, String(n))
                      } catch {
                        /* ignore */
                      }
                      setPageSize(n)
                      setPage(1)
                    }}
                    allowDeselect={false}
                    checkIconPosition="right"
                    styles={{ root: { width: 96 } }}
                  />
                  <PaginationBar
                    page={page}
                    pageCount={totalPages}
                    onPageChange={setPage}
                    disabled={loading}
                    total={list?.total}
                  />
                </Group>
              </Group>
              <Text size="xs" c="dimmed" mt="xs">
                Всего записей: {list?.total ?? 0}
              </Text>
            </Card>

            {loading ? <PageCenterLoader label="Обновление…" minHeight={120} /> : null}

            {!loading && rows.length === 0 ? (
              <EmptyState
                icon={<IconUser size={36} stroke={1.2} />}
                title={searchApplied ? 'Ничего не найдено' : 'Нет пользователей'}
                description={
                  searchApplied
                    ? 'Измените запрос поиска.'
                    : 'Добавьте идентификаторы клиентов для этого проекта.'
                }
              />
            ) : !loading && rows.length > 0 ? (
              <DataTableScroll>
                <DataTable>
                  <DataThead>
                    <DataTr>
                      <DataTh>Пользователь</DataTh>
                      <DataTh>Email</DataTh>
                      <DataTh>Идентификаторы</DataTh>
                      <DataTh style={{ width: 1 }}>Действия</DataTh>
                    </DataTr>
                  </DataThead>
                  <DataTbody>
                    {rows.map((row) => (
                      <DataTr key={row.user_id}>
                        <DataTd>
                          <Text fw={600} size="sm">
                            {row.user_name || '—'}
                          </Text>
                        </DataTd>
                        <DataTd>
                          <Text size="sm" c="dimmed">
                            {row.user_email || '—'}
                          </Text>
                        </DataTd>
                        <DataTd>
                          <Group gap={6} wrap="wrap">
                            {visibleCustomerIdentifiers(row.identifiers).map((id) => (
                              <Badge key={id.id} variant="light" size="sm">
                                {identifierTypeLabel(id.identifier_type)}: {id.identifier_value}
                              </Badge>
                            ))}
                          </Group>
                        </DataTd>
                        <DataTd>
                          <Group gap={4} wrap="nowrap" justify="flex-end">
                            <Button size="compact-xs" variant="light" onClick={() => void openTickets(row)}>
                              Обращения
                            </Button>
                            <Button size="compact-xs" variant="light" onClick={() => void openManage(row, projectId)}>
                              Данные
                            </Button>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              aria-label="Удалить из проекта"
                              onClick={() => removeRow(row)}
                            >
                              <IconTrash size={16} stroke={1.5} />
                            </ActionIcon>
                          </Group>
                        </DataTd>
                      </DataTr>
                    ))}
                  </DataTbody>
                </DataTable>
              </DataTableScroll>
            ) : null}
          </Stack>
        )}
      </Stack>

      <AppModal
        opened={manageOpen}
        onClose={() => {
          setManageOpen(false)
          setSelectedIdentifierIds([])
        }}
        title={
          <Stack gap={0}>
            <Text fw={600}>Управление клиентом</Text>
            <Text size="sm" c="dimmed" fw={400}>
              {manageTitle}
            </Text>
          </Stack>
        }
        size="lg"
        scrollAreaComponent={ScrollArea.Autosize}
      >
        {idLoading ? (
          <PageCenterLoader label="Загрузка" minHeight={120} />
        ) : (
          <Stack gap="md">
            <Card withBorder padding="sm" radius="md">
              <Text fw={600} size="sm" mb="sm">
                Основные данные
              </Text>
              <Stack gap="sm">
                <Group grow>
                  <TextInput
                    label="Имя"
                    value={customerForm.first_name}
                    onChange={(e) => setCustomerForm((s) => ({ ...s, first_name: e.target.value }))}
                  />
                  <TextInput
                    label="Фамилия"
                    value={customerForm.last_name}
                    onChange={(e) => setCustomerForm((s) => ({ ...s, last_name: e.target.value }))}
                  />
                </Group>
                <Group grow>
                  <TextInput
                    label="Email"
                    type="email"
                    value={customerForm.email}
                    onChange={(e) => setCustomerForm((s) => ({ ...s, email: e.target.value }))}
                  />
                  <TextInput
                    label="Телефон"
                    value={customerForm.phone}
                    onChange={(e) => setCustomerForm((s) => ({ ...s, phone: e.target.value }))}
                  />
                </Group>
                <Textarea
                  label="Примечание"
                  minRows={2}
                  value={customerForm.note}
                  onChange={(e) => setCustomerForm((s) => ({ ...s, note: e.target.value }))}
                />
                <Button onClick={() => void saveCustomer()}>Сохранить данные клиента</Button>
              </Stack>
            </Card>

            <Box>
              <Group justify="space-between" align="center" wrap="wrap" gap="xs" mb="xs">
                <Text fw={600} size="sm">
                  Идентификаторы в проекте
                </Text>
                {identifiers.length > 0 ? (
                  <Group gap="xs" wrap="wrap">
                    <Checkbox
                      label="Все"
                      checked={allIdentifiersSelected}
                      indeterminate={someIdentifiersSelected}
                      onChange={() => toggleSelectAllIdentifiers()}
                    />
                    <Button
                      size="compact-xs"
                      variant="light"
                      color="red"
                      disabled={selectedIdentifierIds.length === 0}
                      onClick={() => removeSelectedIdentifiers()}
                    >
                      Удалить выбранные
                      {selectedIdentifierIds.length > 0 ? ` (${selectedIdentifierIds.length})` : ''}
                    </Button>
                  </Group>
                ) : null}
              </Group>
              <Stack gap="xs">
                {identifiers.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    Нет дополнительных данных
                  </Text>
                ) : (
                  identifiers.map((id) => (
                    <Card key={id.id} withBorder padding="xs" radius="md">
                      <Group justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
                        <Checkbox
                          mt={2}
                          aria-label={`Выбрать: ${id.identifier_value}`}
                          checked={selectedIdentifierIds.includes(id.id)}
                          onChange={() => toggleIdentifierSelected(id.id)}
                        />
                        <Box style={{ minWidth: 0, flex: 1 }}>
                          <Text size="sm" fw={500}>
                            {identifierTypeLabel(id.identifier_type)}
                          </Text>
                          <Text size="sm" c="dimmed" style={{ wordBreak: 'break-all' }}>
                            {id.identifier_value}
                          </Text>
                        </Box>
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          aria-label="Удалить"
                          onClick={() => removeIdentifier(id.id)}
                        >
                          <IconTrash size={16} stroke={1.5} />
                        </ActionIcon>
                      </Group>
                    </Card>
                  ))
                )}
              </Stack>
            </Box>

            <Card withBorder padding="sm" radius="md">
              <Text fw={600} size="sm" mb="sm">
                Добавить идентификатор
              </Text>
              <Stack gap="sm">
                <Group grow align="flex-start">
                  <Select
                    label="Тип"
                    data={IDENTIFIER_TYPES}
                    value={newIdType}
                    onChange={(value) => setNewIdType(value || 'email')}
                    allowDeselect={false}
                    checkIconPosition="right"
                  />
                  <TextInput
                    label="Значение"
                    value={newIdValue}
                    onChange={(e) => setNewIdValue(e.target.value)}
                  />
                </Group>
                {newIdType === 'custom' ? (
                  <TextInput
                    label="Отображаемое имя"
                    value={newIdDisplayName}
                    onChange={(e) => setNewIdDisplayName(e.target.value)}
                  />
                ) : null}
                <Button onClick={() => void addIdentifier()}>Добавить</Button>
              </Stack>
            </Card>
          </Stack>
        )}
      </AppModal>

      <AppModal
        opened={ticketsOpen}
        onClose={() => setTicketsOpen(false)}
        title={`Обращения: ${ticketsTitle}`}
        size="lg"
      >
        {ticketsLoading ? (
          <PageCenterLoader label="Загрузка" minHeight={160} />
        ) : ticketsRows.length === 0 ? (
          <Text size="sm" c="dimmed">
            Нет обращений
          </Text>
        ) : (
          <ScrollArea.Autosize mah={420}>
            <DataTable>
              <DataThead>
                <DataTr>
                  <DataTh>Тема</DataTh>
                  <DataTh>Статус</DataTh>
                  <DataTh>Создано</DataTh>
                  <DataTh style={{ width: 1 }} />
                </DataTr>
              </DataThead>
              <DataTbody>
                {ticketsRows.map((t) => (
                  <DataTr key={t.id}>
                    <DataTd>
                      <Text size="sm" lineClamp={2}>
                        {t.subject || '—'}
                      </Text>
                    </DataTd>
                    <DataTd>
                      <Text size="sm">{statusLabel(t.status)}</Text>
                    </DataTd>
                    <DataTd>
                      <Text size="sm" c="dimmed">
                        {t.created_at ? formatDateTimeDot(t.created_at) : '—'}
                      </Text>
                    </DataTd>
                    <DataTd>
                      <ActionIcon
                        variant="subtle"
                        aria-label="Открыть"
                        onClick={() => {
                          setTicketsOpen(false)
                          openTicket(t)
                        }}
                      >
                        <IconExternalLink size={16} stroke={1.5} />
                      </ActionIcon>
                    </DataTd>
                  </DataTr>
                ))}
              </DataTbody>
            </DataTable>
          </ScrollArea.Autosize>
        )}
      </AppModal>

      <AppModal
        opened={addOpen}
        onClose={() => setAddOpen(false)}
        title="Добавить пользователя в проект"
        size="md"
        footer={
          <>
            <ActionButton action="cancel" onClick={() => setAddOpen(false)} />
            <ActionButton action="save" loading={addSaving} onClick={() => void submitAdd()} />
          </>
        }
      >
        <Stack gap="sm">
          <Text size="xs" c="dimmed">
            Проект: {projectName || projectId}. Клиент должен существовать в системе (UUID из карточки обращения
            или из списка ниже).
          </Text>
          <Select
            label="Клиент из проекта"
            description="Уже привязанные к проекту клиенты"
            data={[
              { value: '', label: '— не выбран —' },
              ...customerOptions.map((c) => ({
                value: c.user_id,
                label: `${c.user_name || c.user_id}${c.user_email ? ` · ${c.user_email}` : ''}`,
              })),
            ]}
            value={addCustomerId}
            onChange={(value) => {
              setAddCustomerId(value || '')
              if (value) setAddCustomerManual('')
            }}
            searchable
            allowDeselect={false}
            checkIconPosition="right"
          />
          <TextInput
            label="Или UUID клиента"
            description="Если клиента нет в списке"
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={addCustomerManual}
            onChange={(e) => {
              setAddCustomerManual(e.target.value)
              if (e.target.value.trim()) setAddCustomerId('')
            }}
          />
          <Group grow>
            <Select
              label="Тип идентификатора"
              data={IDENTIFIER_TYPES}
              value={addIdType}
              onChange={(value) => setAddIdType(value || 'email')}
              allowDeselect={false}
              checkIconPosition="right"
            />
            <TextInput
              label="Значение"
              value={addIdValue}
              onChange={(e) => setAddIdValue(e.target.value)}
            />
          </Group>
          {addIdType === 'custom' ? (
            <TextInput
              label="Отображаемое имя"
              value={addDisplayName}
              onChange={(e) => setAddDisplayName(e.target.value)}
            />
          ) : null}
          <Switch label="Активный идентификатор" checked={addActive} onChange={(e) => setAddActive(e.currentTarget.checked)} />
        </Stack>
      </AppModal>
    </PageShell>
  )
}
